/* HC custom — variant popover for the sticky add-to-cart bar.
   Tapping the bar's variant text opens a small panel listing every option
   group; picking a value clicks the matching input in the MAIN variant
   picker, so price/media/availability all update through the existing
   machinery and sold-out values stay hidden for free. The popup closes
   before driving the picker because the bar morphs its children on variant
   change (which would orphan an open panel). Document-level delegation
   keeps the trigger alive through those morphs and editor re-renders.

   Keyboard contract: the panel is inserted right after the trigger's parent
   so tab order matches visual order; focus moves into the panel on open,
   returns to the trigger on every close path that held focus inside, and
   re-lands on the (re-rendered) trigger after the bar's post-selection
   morph. Options are exposed as radios per option group. */

const TRIGGER_SELECTOR = '.hc-sticky-variant-trigger';
const POPUP_SELECTOR = '.hc-sticky-variant-popup';

/** @type {HTMLElement | null} */
let popup = null;

function closePopup() {
  const panel = popup;
  const hadFocusInside = panel?.contains(document.activeElement);
  /* Clear the reference BEFORE removing: removing the focused panel fires its
     focusout handler, which would remove it a second time. Chrome throws
     ("node moved in a 'blur' event handler") and the throw aborted the
     option click, so picking a value in the popup changed nothing. */
  popup = null;
  try {
    panel?.remove();
  } catch (e) {
    /* already detached by a blur handler */
  }
  const trigger = document.querySelector(`${TRIGGER_SELECTOR}[aria-expanded="true"]`);
  trigger?.setAttribute('aria-expanded', 'false');
  // Removing the focused node drops keyboard users to <body>; mouse
  // outside-clicks keep their own focus target.
  if (hadFocusInside) /** @type {HTMLElement | null} */ (trigger)?.focus({ preventScroll: true });
}

/**
 * After a selection the sticky bar morphs its children from the server
 * response, replacing the trigger node - move focus onto the fresh trigger
 * once it lands so keyboard users continue from where they were.
 * @param {Element} bar
 */
function refocusAfterMorph(bar) {
  const observer = new MutationObserver(() => {
    const trigger = /** @type {HTMLElement | null} */ (bar.querySelector(TRIGGER_SELECTOR));
    if (!trigger) return;
    observer.disconnect();
    clearTimeout(timeout);
    trigger.focus({ preventScroll: true });
  });
  observer.observe(bar, { childList: true, subtree: true });
  const timeout = setTimeout(() => observer.disconnect(), 4000);
}

/**
 * @param {HTMLElement} sticky - the <sticky-add-to-cart> element
 * @returns {HTMLElement | null}
 */
function buildPopup(sticky) {
  const productId = sticky.dataset.productId;
  const picker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
  if (!picker) return null;

  const bar = sticky.querySelector('[ref="stickyBar"]');
  const panel = document.createElement('div');
  panel.className = 'hc-sticky-variant-popup color-custom-popover';
  panel.setAttribute('role', 'group');
  panel.setAttribute('aria-label', 'Change options');
  panel.tabIndex = -1;

  for (const fieldset of picker.querySelectorAll('fieldset')) {
    const inputs = [...fieldset.querySelectorAll('input[type="radio"]')];
    if (!inputs.length) continue;

    const group = document.createElement('div');
    group.className = 'hc-sticky-variant-popup__group';
    /* Matches the pills below: a group of toggle buttons, not a radiogroup
       (see the note at the option elements). aria-labelledby still names it. */
    group.setAttribute('role', 'group');

    // The legend's first text node is the option name; the trailing span
    // holds the selected value and is not part of the label.
    const legendName = fieldset.querySelector('legend')?.childNodes[0]?.textContent?.trim();
    if (legendName) {
      const label = document.createElement('p');
      label.className = 'hc-sticky-variant-popup__label';
      label.id = `hc-svp-label-${Math.random().toString(36).slice(2, 8)}`;
      label.textContent = legendName;
      group.setAttribute('aria-labelledby', label.id);
      group.appendChild(label);
    }

    const row = document.createElement('div');
    row.className = 'hc-sticky-variant-popup__options';
    for (const input of inputs) {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'hc-sticky-variant-popup__option';
      option.textContent = input.value;
      /* HC a11y: these were role="radio" inside a role="radiogroup", which
         tells a screen-reader user "radio 1 of 8 - use the arrow keys". No
         arrow handler existed, so the promise was false; and selecting here
         closes the popup, so arrow-to-select (the required radio behaviour)
         would dismiss the picker on the first keypress. Plain toggle buttons
         describe what these actually are, and Tab already reaches them. */
      option.setAttribute('aria-pressed', input.checked ? 'true' : 'false');
      /* Sold-out values only reach this list when the picker's "Show
         sold-out colours/sizes" switch is on; mark them like the picker
         does (greyed, struck) and say so to screen readers. Still
         selectable - choosing one shows Sold out, as on the page. */
      if (input.dataset.optionAvailable === 'false') {
        option.classList.add('hc-sticky-variant-popup__option--unavailable');
        option.setAttribute('aria-label', `${input.value}, sold out`);
      }
      option.addEventListener('click', () => {
        closePopup();
        if (!input.checked) {
          /* Click the radio itself, not its label: a label click focuses
             the input, which scrolled the page back up to the main picker
             and hid the sticky bar. A programmatic input click still fires
             the 'change' the picker listens for, without moving focus. */
          input.click();
          if (bar) refocusAfterMorph(bar);
        }
      });
      row.appendChild(option);
    }
    group.appendChild(row);
    panel.appendChild(group);
  }

  if (!panel.children.length) return null;

  // Tabbing past the panel means the user has moved on - close and release.
  // Only when focus lands somewhere: Safari and Firefox on the Mac don't focus
  // a button on click, so pressing the trigger to close the panel dropped
  // focus to nothing, this closed it, and that same click then reopened it
  // (owner, 2026-10-06). Clicks are the document listener's job below.
  panel.addEventListener('focusout', (event) => {
    const next = /** @type {Node | null} */ (event.relatedTarget);
    if (!next || panel.contains(next) || next === document.querySelector(TRIGGER_SELECTOR)) return;
    if (popup === panel) {
      popup = null;
      panel.remove();
      document
        .querySelector(`${TRIGGER_SELECTOR}[aria-expanded="true"]`)
        ?.setAttribute('aria-expanded', 'false');
    }
  });

  return panel;
}

document.addEventListener('click', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  const trigger = target.closest?.(TRIGGER_SELECTOR);

  if (trigger) {
    if (popup) {
      closePopup();
      return;
    }
    const sticky = trigger.closest('sticky-add-to-cart');
    const built = sticky instanceof HTMLElement ? buildPopup(sticky) : null;
    const anchor = trigger.closest('.sticky-add-to-cart__variant');
    if (!built || !anchor) return;
    // Inserted right after the trigger's parent so DOM order matches visual
    // order (before Add to cart), then focused so Tab lands on the options.
    anchor.insertAdjacentElement('afterend', built);
    popup = built;
    trigger.setAttribute('aria-expanded', 'true');
    built.focus({ preventScroll: true });
    return;
  }

  if (popup && !target.closest?.(POPUP_SELECTOR)) closePopup();
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closePopup();
});
